import { newE2EPage } from "@stencil/core/testing";

const html = `<uic-combobox>
    <uic-list-item value="1">Value 1</uic-list-item>
    <uic-list-item value="2">Value 2</uic-list-item>
    <uic-list-item value="3">Other</uic-list-item>
    <uic-list-item value="4">Mother</uic-list-item>
    <uic-list-item value="5">Value Other</uic-list-item>
</uic-combobox>`;

describe("uic-combobox", () => {
    it("should emit uicChange event when value is changed from outside", async () => {
        const page = await newE2EPage({
            html: `
            <uic-combobox>
                <uic-list-item value="1">Value 1</uic-list-item>
                <uic-list-item value="2">Value 2</uic-list-item>
                <uic-list-item value="3">Value 3</uic-list-item>
            </uic-combobox>`
        });
        await page.setViewport({
            width: 900,
            height: 600
        });
        const uicChange = await page.spyOnEvent("uicChange");

        const combobox = await page.find("uic-combobox");
        await combobox.setProperty("value", "2");
        await page.waitForChanges();

        expect(uicChange).toHaveReceivedEventTimes(1);
        expect(uicChange).toHaveReceivedEventDetail("2");
    });

    it("should emit uicChange event when x button is clicked", async () => {
        const page = await newE2EPage({
            html: `<uic-combobox value="2">
                <uic-list-item value="1">Value 1</uic-list-item>
                <uic-list-item value="2">Value 2</uic-list-item>
                <uic-list-item value="3">Value 3</uic-list-item>
            </uic-combobox>`
        });
        const uicChange = await page.spyOnEvent("uicChange");

        const combobox = await page.find("uic-combobox");
        const input = await page.find(".uic-input-field__input");

        expect(await input.getProperty("value")).toEqual("Value 2");

        await combobox.setProperty("value", "2");
        await page.waitForChanges();

        let clearButton = await page.find(".uic-input-field__clear-button");
        await clearButton.click();
        await page.waitForChanges();

        expect(uicChange).toHaveReceivedEventTimes(1);
        expect(uicChange).toHaveReceivedEventDetail("");

        clearButton = await page.find(".uic-input-field__clear-button");
        expect(clearButton).toBeNull();
    });

    it("should filter list items correctly when typing in the input field", async () => {
        const page = await newE2EPage({ html });
        const uicChange = await page.spyOnEvent("uicChange");
        const input = await page.find(".uic-input-field__input");

        expect(await input.getProperty("value")).toEqual("");

        await input.press("o");
        await input.press("t");
        await input.press("h");

        expect(await input.getProperty("value")).toEqual("oth");
        expect(uicChange).toHaveReceivedEventTimes(0);

        const filteredListItems = await page.findAll("uic-list-item:not(.uic-hidden)");
        expect(filteredListItems.length).toBe(3);
    });

    it("should filter list items correctly when typing in the input field while accepting arbitrary input", async () => {
        const page = await newE2EPage({
            html: html.replace(/(<uic-combobox)/, "$1 arbitrary-input")
        });
        const uicChange = await page.spyOnEvent("uicChange");
        const input = await page.find(".uic-input-field__input");

        expect(await input.getProperty("value")).toEqual("");

        await input.press("o");
        await input.press("t");
        await input.press("h");

        expect(uicChange).toHaveReceivedEventTimes(0);
        expect(await input.getProperty("value")).toEqual("oth");

        const filteredListItems = await page.findAll("uic-list-item:not(.uic-hidden)");
        expect(filteredListItems.length).toBe(3);
    });

    it("emits uicInput event and collects all input when typing into input field", async () => {
        const page = await newE2EPage({ html });
        const uicInput = await page.spyOnEvent("uicInput");
        const uicChange = await page.spyOnEvent("uicChange");
        const input = await page.find("uic-combobox .uic-input-field__input");

        await input.press("a");
        expect(uicInput).toHaveReceivedEventTimes(1);
        expect(uicChange).toHaveReceivedEventTimes(0);
        expect(await input.getProperty("value")).toEqual("a");

        await input.press("b");
        expect(uicInput).toHaveReceivedEventTimes(2);
        expect(uicChange).toHaveReceivedEventTimes(0);
        expect(await input.getProperty("value")).toEqual("ab");

        await input.press("c");
        expect(uicInput).toHaveReceivedEventTimes(3);
        expect(uicChange).toHaveReceivedEventTimes(0);
        expect(await input.getProperty("value")).toEqual("abc");
    });

    it("accepts arbitrary input but drops any mismatching value when assigned by default", async () => {
        const page = await newE2EPage({ html });
        const uicInput = await page.spyOnEvent("uicInput");
        const uicChange = await page.spyOnEvent("uicChange");
        const combobox = await page.find("uic-combobox");
        const input = await combobox.find(".uic-input-field__input");

        await input.press("a");
        await input.press("b");
        await input.press("c");

        expect(uicInput).toHaveReceivedEventTimes(3);
        expect(uicChange).toHaveReceivedEventTimes(0);
        expect(await input.getProperty("value")).toEqual("abc");

        await combobox.setProperty("value", "abc");
        await page.waitForChanges();

        expect(await input.getProperty("value")).toEqual("");
    });

    it("accepts arbitrary input and accepts mismatching value when assigned on demand", async () => {
        const page = await newE2EPage({
            html: html.replace(/(<uic-combobox)/, "$1 arbitrary-input")
        });
        const uicInput = await page.spyOnEvent("uicInput");
        const uicChange = await page.spyOnEvent("uicChange");
        const combobox = await page.find("uic-combobox");
        const input = await combobox.find(".uic-input-field__input");

        await input.press("a");
        await input.press("b");
        await input.press("c");

        expect(uicInput).toHaveReceivedEventTimes(3);
        expect(uicChange).toHaveReceivedEventTimes(0);
        expect(await input.getProperty("value")).toEqual("abc");

        await combobox.setProperty("value", "abc");
        await page.waitForChanges();

        expect(uicInput).toHaveReceivedEventTimes(3);
        expect(uicChange).toHaveReceivedEventTimes(1);

        expect(await input.getProperty("value")).toEqual("abc");
    });

    it("should filter list items correctly when assigning arbitrary input field", async () => {
        const page = await newE2EPage({
            html: html.replace(/(<uic-combobox)/, "$1 arbitrary-input")
        });
        const uicInput = await page.spyOnEvent("uicInput");
        const uicChange = await page.spyOnEvent("uicChange");
        const combobox = await page.find("uic-combobox");
        const input = await combobox.find(".uic-input-field__input");

        expect(await input.getProperty("value")).toEqual("");

        await combobox.setProperty("value", "oth");
        await page.waitForChanges();

        expect(uicInput).toHaveReceivedEventTimes(0);
        expect(uicChange).toHaveReceivedEventTimes(1);
        expect(await input.getProperty("value")).toEqual("oth");

        const filteredListItems = await page.findAll("uic-list-item:not(.uic-hidden)");
        expect(filteredListItems.length).toBe(3);
    });
});
